import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Button, TextField, Grid } from "@material-ui/core";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import clsx from "clsx";
import Title from "../../Title";
import ColoredLine from "../../ColoredLine";
import MuiPhoneNumber from "material-ui-phone-number";
import { useSelector } from "react-redux";
import ValetParkingService from "../../../services/ValetParkingService";
import apiClient from "../../../auth/apiClient";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import { useCountry } from '../../../hooks/useCountry';
import CashierReceiptService from "../../../services/CashierReceiptService";

export const ReceiptType = {
  EMAIL: 1,
  SMS: 2,
};

const validationSchemas = {
  [ReceiptType.EMAIL]: yup.object().shape({
    email: yup
      .string()
      .email("Please enter a valid email address")
      .required("Please enter a valid email address"),
  }),
  [ReceiptType.SMS]: yup.object().shape({
    phone: yup
      .string()
      .trim()
      .transform((currentval) => {
        if (currentval === "+1") {
          return null;
        }
        return currentval;
      })
      .nullable()
      .test("valid-phone", "Phone number must consist of 10 digits", (val) => {
        if (!val) return true;
        const digits = val.replace(/\D/g, "");
        const phoneNumber = digits.slice(-10);
        return phoneNumber.length === 10 && digits.length >= 11;
      })
      .required("Please enter a valid phone number")
  }),
};

const CashieredDeviceSummaryReceipt = ({ receiptType, onCancel, onReceiptSent, classes }) => {
  const enqueueSnackbar = useEnqueueSnackbar();
  const notificationStyle = useSelector((state) => {
    return {
      small: state.shiftSession?.smallScreen,
      toastLocation: state.shiftSession?.toastLocation,
    };
  });
  
  const cashierReceiptService = new CashierReceiptService(apiClient);
  const credentialID = useSelector((state) => state.shiftSession?.transaction?.credential?.credentialID);
  
  const { countries } = useCountry();

  const entityID = useSelector((state) => state.shiftSession?.entityID);

  const ticketNumber = useSelector((state) => state.shiftSession?.transaction?.credential?.ticketID);

  const schema = validationSchemas[receiptType];

  const { control, handleSubmit, formState: { errors, isValid, isSubmitting }, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: receiptType === ReceiptType.EMAIL ? { email: "" } : { phone: "" },
    mode: "onChange",
  });

  useEffect(() => {
    const fetchArrivalDetails = async () => {
      const valetParkingService = new ValetParkingService(apiClient);
      try {
        const result = await valetParkingService.getArrivalDetails(entityID, ticketNumber);
        if (result?.data?.arrival) {
          const arrivalInfo = result.data.arrival;
          const updatedValues = receiptType === ReceiptType.EMAIL
            ? { email: arrivalInfo.email || "" }
            : { phone: arrivalInfo.phoneNumber || "" };
          reset(updatedValues);
        }
      } catch (error) {
        triggerEnqueueSnackBar("Failed to fetch arrival details", "error", "failedToFetchArrivalDetails");
      }
    };

    fetchArrivalDetails();
  }, [entityID, ticketNumber, receiptType, reset]);

  const getHeaderText = () => {
    return receiptType === ReceiptType.EMAIL
      ? "Please Enter Email Address"
      : "Please Enter Phone Number";
  };

  const getFormContent = () => {
    if (receiptType === ReceiptType.EMAIL) {
      return (
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="email"
              label="Email Address *"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message || " "}
              autoFocus
            />
          )}
        />
      );
    } else {
      return (
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <MuiPhoneNumber
              {...field}
              id="phone"
              label="Phone Number *"
              variant="outlined"
              fullWidth
              defaultCountry="us"
              regions={["north-america"]}
              disableAreaCodes
              countryCodeEditable={false}
              onlyCountries={countries.map((country) => country.alpha2.toLowerCase())}
              excludeCountries={["pm", "bm"]}
              error={!!errors.phone}
              helperText={errors.phone?.message || " "}
              autoFocus
            />
          )}
        />
      );
    }
  };

  const handleFormSubmit = async (formData) => {
    if (receiptType === ReceiptType.SMS) {
      await sendSMSReceipt(formData.phone);
    } else {
      await sendEmailReceipt(formData.email);
    }
  };

  const sendSMSReceipt = async (phone) => {
    try {
      await cashierReceiptService.sendSMSReceipt({
        EntityID: entityID,
        CredentialID: credentialID,
        To: phone,
      });
      triggerEnqueueSnackBar("Your receipt has been sent successfully", "success");
      onReceiptSent();
    } catch {
      triggerEnqueueSnackBar(
        "There was an issue sending your text, please try again",
        "error",
        "failedToSendSMS"
      );
    }
  };

  const sendEmailReceipt = async (email) => {
    try {
      await cashierReceiptService.sendEmailReceipt({
        EntityID: entityID,
        CredentialID: credentialID,
        ToEmail: email,
      });
      triggerEnqueueSnackBar("Your receipt has been emailed successfully", "success");
      onReceiptSent();
    } catch {
      triggerEnqueueSnackBar(
        "There was an issue sending your email, please try again",
        "error",
        "failedToSendEmail"
      );
    }
  };

  const triggerEnqueueSnackBar = (msg, variant, tag) => {
    enqueueSnackbar(msg, {
        variant: variant,
        tag: tag,
        anchorOrigin: notificationStyle.toastLocation,
        fullwidth: notificationStyle.small,
    });
};

  return (
    <div>
      <Title className={clsx(`${receiptType}-receipt-title`)}>
        {getHeaderText()}
      </Title>
      <ColoredLine className={classes.coloredLineSpacing} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {getFormContent()}
        </Grid>
        <Grid item xs={12} className={classes.buttonContainer}>
          <Button
            color="primary"
            name="submit"
            variant="contained"
            onClick={handleSubmit(handleFormSubmit)}
            data-testid="sendReceiptBtn"
            className="send-receipt-btn"
            disabled={!isValid || isSubmitting}
          >
            Send Receipt
          </Button>
          <Button
            variant="contained"
            onClick={onCancel}
            data-testid="cancelBtn"
            className="cancel-receipt-btn"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

CashieredDeviceSummaryReceipt.defaultProps = {
  onCancel: () => {},
  onReceiptSent: () => {},
  classes: {},
};

CashieredDeviceSummaryReceipt.propTypes = {
  receiptType: PropTypes.oneOf([ReceiptType.EMAIL, ReceiptType.SMS]).isRequired,
  onCancel: PropTypes.func,
  onReceiptSent: PropTypes.func,
  classes: PropTypes.object,
};

export default CashieredDeviceSummaryReceipt;
